import { Component } from '@opten/compiler/metadata/component';
import { OnInit } from '@opten/compiler/metadata/lifecycle';
import getParentWithClass from '@opten/dom/get-parent-with-class';
import { ComponentRef } from '@opten/compiler/metadata/di';
import { ShopContext, wantsToBeGuestStorage } from '../shop/shop-context';
import { ModalComponent } from '../../components/modal';
import dispatchCustomEvent from '@opten/events/dispatch-custom-event';
import { take } from 'rxjs/operators';

export interface InputCounterComponentOptions {
	selectors?: {
		increase: string;
		decrease: string;
		input: string;
		inputDelete: string;
		wrapper: string;
	};
}
@Component({ className: 'js-input-counter', observe: true })
export class InputCounterComponent implements OnInit {
	private increaseElement: HTMLElement;
	private decreaseElement: HTMLElement;
	private inputElement: HTMLInputElement;
	private _options: InputCounterComponentOptions = {
		selectors: {
			increase: 'js-input-counter__increase',
			decrease: 'js-input-counter__decrease',
			input: 'js-input-counter__value',
			inputDelete: 'js-input-counter__value--delete',
			wrapper: 'js-input-counter__wrapper'
		}
	};
	private stepSize: number;

	@ComponentRef(ShopContext) shopContext: ShopContext;

	constructor(private element: HTMLElement, options?: InputCounterComponentOptions) {
		this._options = { ...this._options, ...options };

		this.increaseElement = this.element.getElementsByClassName(this._options.selectors.increase)[0] as HTMLElement;
		this.decreaseElement = this.element.getElementsByClassName(this._options.selectors.decrease)[0] as HTMLElement;
		this.inputElement = this.element.getElementsByClassName(this._options.selectors.input)[0] as HTMLInputElement;
		const inputElementWrapper = getParentWithClass(this.element, this._options.selectors.wrapper);
		if (inputElementWrapper) {
			const inputElementDelete = Array.from(inputElementWrapper.getElementsByClassName(this._options.selectors.inputDelete));
			if (inputElementDelete && inputElementDelete.length > 0) {
				inputElementDelete.forEach(element => {
					element.addEventListener('click', () => this.deleteInput());
				});
			}
		}
	}
	onInit(): void {
		this.stepSize = 1;

		this.increaseElement.addEventListener('click', () => {
			if (!this.inputElement.disabled) {
				this.increaseInput();
			}
		});

		this.decreaseElement.addEventListener('click', () => {
			if (!this.inputElement.disabled) {
				this.decreaseInput();
			}
		});

		this.inputElement.addEventListener('change', () => {
			this.setInputNormalizedValue();
			this.inputElement.dispatchEvent(new Event('keyup'));
		});
	}

	private deleteInput(): void {
		this.setInputValue('');
	}

	private increaseInput(): void {
		let val = this.stepSize.toString();
		const value = this.getCurrentValue();

		if (!isNaN(value)) {
			val = (value + this.stepSize).toString();
		}
		this.setInputValue(val);
	}

	private decreaseInput(): void {
		let val = '';
		const value = this.getCurrentValue();

		if (!isNaN(value)) {
			if (value > this.stepSize) {
				val = (value - this.stepSize).toString();
			} else {
				val = '0';
			}
		}
		this.setInputValue(val);
	}

	private setInputValue(val: string) {
		this.inputElement.value = val;
		this.inputElement.dispatchEvent(new Event('change'));
	}

	private setInputNormalizedValue() {
		const value = this.getCurrentValue();
		this.inputElement.value = value + '';
	}

	private getCurrentValue(): number {
		const current = this.inputElement.value;
		if (current) {
			const value = parseInt(current, 10);
			if (!isNaN(value)) {
				return this.normalizeValueToStepSize(value);
			}
		}
		return 0;
	}

	private normalizeValueToStepSize(value: number): number {
		const newValue = parseInt((value / this.stepSize).toString(), 10) * this.stepSize;
		if (value >= this.stepSize) {
			value = newValue;
		} else {
			value = value > 0 ? this.stepSize : 0;
		}
		return value;
	}
}
